import { useState, useMemo } from "react";
import CryptoJS from "crypto-js";
import parse from "html-react-parser";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import PaginationArrowLeft from "../../assets/images/pagination-arrow-left.svg";
import PaginationArrowLeftLeft from "../../assets/images/pagination-arrow-left-left.svg";
import PaginationArrowRight from "../../assets/images/pagination-arrow-right.svg";
import PaginationArrowRightRight from "../../assets/images/pagination-arrow-right-right.svg";

import SortingArrows from "../../assets/images/sorting-arrows.svg";

function normalizeClaimedUsers(claimedBy) {
  if (!claimedBy) {
    return [];
  }
  return Array.isArray(claimedBy) ? claimedBy : [claimedBy];
}

function getClaimedUsers(row) {
  return normalizeClaimedUsers(row.original?.claimed_by);
}

function formatClaimedUsers(row, field) {
  return normalizeClaimedUsers(row.claimed_by)
    .map((user) => user[field])
    .filter(Boolean)
    .join(", ");
}

function encryptUserId(id) {
  const key = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY);
  const iv = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_IV);

  return CryptoJS.AES.encrypt(id.toString(), key, { iv: iv })
    .toString()
    .replace(/\+/g, "p1L2u3S")
    .replace(/\//g, "s1L2a3S4h")
    .replace(/=/g, "e1Q2u3A4l");
}

function formatExpiration(expiresAt) {
  if (!expiresAt) {
    return "Bez terminu";
  }

  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) {
    return "Nieprawidłowa data";
  }

  return date.toISOString().slice(0, 10);
}

function renderUserLinks(row, path, label) {
  const users = getClaimedUsers(row).filter((user) => user.id);
  if (!users.length) {
    return null;
  }

  return (
    <>
      {users.map((user) => {
        const encryptCode = encryptUserId(user.id);
        const linkLabel =
          users.length > 1 && user.username
            ? `${label}: ${user.username}`
            : label;

        return (
          <div key={user.id}>
            <a
              href={`${process.env.REACT_APP_API_URL}/${path}/${encryptCode}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkLabel}
              <span className="sr-only">
                (link otwiera się w nowej karcie)
              </span>
            </a>
          </div>
        );
      })}
    </>
  );
}

const DashboardTable = ({ data }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "campaign_name",
        header: "Kampania",
      },
      {
        accessorKey: "code",
        header: "Kod",
      },
      {
        accessorKey: "is_reusable",
        header: "Typ",
        cell: (info) => (info.getValue() ? "Wielorazowy" : "Jednorazowy"),
      },
      {
        accessorKey: "is_claimed",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="c-status c-status--occupied">Wykorzystany</span>
          ) : (
            <span className="c-status c-status--free">Wolny</span>
          ),
      },
      {
        accessorKey: "expires_at",
        header: "Ważny do",
        cell: (info) => formatExpiration(info.getValue()),
      },
      {
        accessorFn: (row) => formatClaimedUsers(row, "username"),
        id: "claimed-by",
        header: "Wykorzystane przez",
      },
      {
        accessorFn: (row) => formatClaimedUsers(row, "id"),
        header: "Dopasowane zawody",
        id: "careers-list",
        cell: (info) =>
          renderUserLinks(
            info.row,
            "profile/careers-list",
            "Pokaż dopasowane zawody"
          ),
      },
      {
        accessorFn: (row) => formatClaimedUsers(row, "id"),
        header: "Profil",
        id: "profile",
        cell: (info) => renderUserLinks(info.row, "profile", "Pokaż profil"),
      },
    ],
    []
  );

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: { sorting: sorting, globalFilter: filtering },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <>
      <label className="sr-only" htmlFor="dashboardTableSearchInput">
        Wyszukaj:
      </label>
      <input
        type="text"
        id="dashboardTableSearchInput"
        name="dashboardTableSearch"
        className="c-dashboard__input"
        value={filtering}
        placeholder="Wyszukaj..."
        onChange={(e) => setFiltering(e.target.value)}
      />
      <div className="c-dashboard__table">
        <table>
          <thead>
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {
                      {
                        asc: parse(
                          `<img src=${SortingArrows} alt="Sortuj malejąco" />`
                        ),
                        desc: parse(
                          `<img src=${SortingArrows} alt="Sortuj rosnąco" />`
                        ),
                      }[header.column.getIsSorted() ?? null]
                    }
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table?.getRowModel()?.rows?.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="c-dashboard__pagination">
        <button
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
          aria-label="Przejdź do pierwszej strony"
        >
          <img src={PaginationArrowLeftLeft} alt="" />
        </button>
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          aria-label="Poprzednia strona"
        >
          <img src={PaginationArrowLeft} alt="" />
        </button>
        <div>
          <label className="sr-only" htmlFor="dashboardTablePageInput">
            Numer strony:
          </label>
          <input
            type="number"
            id="dashboardTablePageInput"
            name="dashboardTablePage"
            className="c-dashboard-pagination__input"
            min={1}
            max={table.getPageCount()}
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
          />{" "}
          z {table.getPageCount()}
        </div>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          aria-label="Następna strona"
        >
          <img src={PaginationArrowRight} alt="" />
        </button>
        <button
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
          aria-label="Przejdź do ostatniej strony"
        >
          <img src={PaginationArrowRightRight} alt="" />
        </button>
      </div>
    </>
  );
};

export default DashboardTable;
